/* Panneau Barème (blocs 06-A/B) — partagé entre l'éditeur de contenu et la page
   de structuration post-import (spec 11). */

import { useState } from 'react';
import { SlowNotice } from './chat-turn-notices';
import { CHAT_SLOW_NOTICE, chatErrorMessage } from '../lib/chat-errors';
import { SLOW_RESPONSE_MS } from '../lib/sse';
import { api } from '../lib/api';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { LatexInline } from './math-node-views';
import type { ContentSectionRow } from './content-sections-editor';
import type { ContentDocumentKind } from '../lib/editor-chat';
import { gradingOf } from '../lib/grading-scale';

export const NOTED_TYPES = new Set(['qcm', 'question_with_answer']);

/** Barème d'une section notée : nombre fini, ou null si absent. */
export const baremeOf = (s: ContentSectionRow): number | null => {
  if (!NOTED_TYPES.has(s.type)) return null;
  const b = (s.content as { bareme?: unknown } | undefined)?.bareme;
  return typeof b === 'number' && Number.isFinite(b) ? b : null;
};

const evalPillBase = "inline-flex items-center gap-[5px] px-[10px] py-[3px] rounded-full text-[12px] font-semibold leading-[1.5] before:content-[''] before:w-[6px] before:h-[6px] before:rounded-full before:bg-current";

interface RubricDraft { section_id: string; title: string; bareme: number; rules: string }

/** Heuristique de provenance : une section notée dont la réponse/les règles de
 *  correction sont déjà renseignées a reçu le corrigé (EXAM_ANSWER_KEY_ADDENDUM
 *  ne complète QUE ces champs, jamais le sujet) — sinon, sujet seul. */
const hasAnswerContent = (s: ContentSectionRow): boolean => {
  const c = s.content as { answer?: unknown; correction_rules?: unknown } | undefined;
  const nonEmpty = (v: unknown) => typeof v === 'string' && v.trim().length > 0;
  return nonEmpty(c?.answer) || nonEmpty(c?.correction_rules);
};

export function EvaluationPanel({ documentId, kind, sections, initialRules, editable, onApplied, hasAnswerKeyImport }: {
  documentId: string; kind: ContentDocumentKind; sections: ContentSectionRow[]; initialRules: string; editable: boolean; onApplied: () => void;
  /** Un corrigé a été importé pour ce document — affiche la provenance
   *  sujet/corrigé de chaque item noté (page de structuration post-import, spec 11). */
  hasAnswerKeyImport?: boolean;
}) {
  const [rules, setRules] = useState(initialRules);
  const [savedRules, setSavedRules] = useState(true);
  const [mode, setMode] = useState<'idle' | 'loading' | 'review'>('idle');
  const [slow, setSlow] = useState(false);
  const [draft, setDraft] = useState<RubricDraft[]>([]);
  const [globalDraft, setGlobalDraft] = useState('');
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState(false);

  const noted = sections.filter((s) => NOTED_TYPES.has(s.type));
  const missing = noted.filter((s) => baremeOf(s) === null && gradingOf(s.content).mode === 'numeric');
  const total = noted.reduce((sum, s) => sum + (gradingOf(s.content).mode === 'scale' ? 0 : (baremeOf(s) ?? 0)), 0);
  const draftTotal = draft.reduce((s, i) => s + (Number.isFinite(i.bareme) ? i.bareme : 0), 0);

  const saveGlobalRules = async () => {
    try {
      await api.content.update.mutate({ id: documentId, patch: { globalCorrectionRules: rules.trim() || null } });
      setSavedRules(true);
    } catch {
      /* réessai au prochain blur */
    }
  };

  const suggest = async () => {
    setMode('loading');
    setError(null);
    setSlow(false);
    const slowTimer = window.setTimeout(() => setSlow(true), SLOW_RESPONSE_MS);
    try {
      const r = await api.content.suggestRubric.mutate({ id: documentId });
      setDraft(r.items as RubricDraft[]);
      setGlobalDraft((r as { global_rules: string }).global_rules);
      setMode('review');
    } catch (e) {
      setError(chatErrorMessage(e));
      setMode('idle');
    } finally {
      window.clearTimeout(slowTimer);
      setSlow(false);
    }
  };

  const apply = async () => {
    setApplying(true);
    setError(null);
    try {
      await api.content.applyRubric.mutate({
        documentId,
        items: draft.map((d) => ({ sectionId: d.section_id, bareme: d.bareme, rules: d.rules })),
        globalRules: globalDraft,
      });
      setRules(globalDraft);
      setSavedRules(true);
      setMode('idle');
      setFlash(true);
      window.setTimeout(() => setFlash(false), 2500);
      onApplied();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setApplying(false);
    }
  };

  const setItem = (id: string, patch: Partial<RubricDraft>) =>
    setDraft((prev) => prev.map((i) => (i.section_id === id ? { ...i, ...patch } : i)));

  return (
    <section className="flex flex-col gap-[14px] p-[18px] bg-card border border-border rounded-card">
      <header className="flex items-center justify-between gap-3 pb-[14px] border-b border-border">
        <div className="flex flex-col gap-[7px] min-w-0 items-start">
          <span className="text-[11px] font-bold tracking-[0.09em] uppercase text-muted-foreground">
            Barème{mode === 'review' ? ' · proposition Naïa' : ''}
          </span>
          {noted.length > 0 &&
            (missing.length === 0 ? (
              <span className={cn(evalPillBase, "text-[#1a8a5a] bg-[color-mix(in_srgb,#1a8a5a_13%,transparent)]")}>Barème complet</span>
            ) : (
              <span className={cn(evalPillBase, "text-[#b4690e] bg-[color-mix(in_srgb,#b4690e_14%,transparent)]")}>
                {missing.length} sans barème{kind === 'exam' ? ' · requis pour corriger' : ''}
              </span>
            ))}
        </div>
        <div className="flex-shrink-0 flex flex-col items-end leading-none">
          <span className="text-[34px] font-extrabold tracking-[-0.02em] text-foreground [font-variant-numeric:tabular-nums]">
            {mode === 'review' ? draftTotal : total}
          </span>
          <span className="mt-[3px] text-[11px] font-semibold tracking-[0.06em] uppercase text-muted-foreground">points</span>
        </div>
      </header>

      {error && <Alert>{error}</Alert>}
      {flash && <div className="p-[9px_12px] rounded-[10px] text-[13px] font-semibold text-[#1a8a5a] bg-[color-mix(in_srgb,#1a8a5a_12%,transparent)]">✓ Barème &amp; règles appliqués</div>}

      {hasAnswerKeyImport && mode === 'idle' && noted.length > 0 && (
        <div className="flex flex-col gap-[8px]">
          {noted.map((s) => {
            const fromAnswerKey = hasAnswerContent(s);
            return (
              <div key={s.id} className="flex items-center justify-between gap-2 pb-[8px] border-b border-border last:border-b-0 last:pb-0">
                <span className="text-[13px] font-semibold text-foreground [overflow-wrap:anywhere] min-w-0">
                  {s.title ? <LatexInline text={s.title} /> : 'Section sans titre'}
                </span>
                <span
                  className={cn(
                    evalPillBase,
                    "shrink-0",
                    fromAnswerKey
                      ? "text-naia-blue bg-naia-sky"
                      : "text-[var(--naia-text-secondary)] bg-[var(--naia-card-alt)]",
                  )}
                >
                  {fromAnswerKey ? 'Sujet + corrigé' : 'Sujet seul'}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {mode === 'review' ? (
        <>
          <p className="m-0 text-[13px] text-muted-foreground">Proposition de Naïa — ajustez avant d'appliquer.</p>
          <div className="flex flex-col gap-[10px]">
            {draft.map((it) => (
              <div
                key={it.section_id}
                className="grid grid-cols-[1fr_auto] [grid-template-areas:'title_bareme'_'rules_rules'] gap-[10px_14px] pb-[10px] border-b border-border last:border-b-0 last:pb-0 min-[560px]:grid-cols-[minmax(0,1fr)_auto_minmax(0,1.5fr)] min-[560px]:[grid-template-areas:'title_bareme_rules'] min-[560px]:items-start min-[560px]:gap-[12px_16px]"
              >
                <div className="[grid-area:title] self-center text-[14px] font-bold text-foreground [overflow-wrap:anywhere] min-[560px]:self-center">{it.title ? <LatexInline text={it.title} /> : 'Section sans titre'}</div>
                <label className="[grid-area:bareme] self-center inline-flex items-baseline gap-[5px] text-[11px] font-bold tracking-[0.05em] uppercase text-muted-foreground min-[560px]:self-center">
                  <input
                    type="number"
                    min={0}
                    step={0.5}
                    value={Number.isFinite(it.bareme) ? it.bareme : ''}
                    onChange={(e) => setItem(it.section_id, { bareme: Number(e.target.value) })}
                    className="w-[58px] p-[6px_9px] text-[15px] font-bold text-right [font-variant-numeric:tabular-nums] border border-border rounded-[9px] bg-card text-foreground focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-1 focus-visible:border-transparent"
                  />
                  <span>pts</span>
                </label>
                <Textarea
                  className="[grid-area:rules] p-[9px_11px] bg-[var(--naia-bg,transparent)]"
                  value={it.rules}
                  rows={2}
                  placeholder="Règle de correction…"
                  onChange={(e) => setItem(it.section_id, { rules: e.target.value })}
                />
              </div>
            ))}
          </div>
          <label className="flex flex-col gap-[6px] text-[11px] font-bold tracking-[0.06em] uppercase text-muted-foreground">
            Règles globales du document
            <Textarea className="p-[9px_11px] bg-[var(--naia-bg,transparent)]" value={globalDraft} rows={2} onChange={(e) => setGlobalDraft(e.target.value)} />
          </label>
          <div className="flex items-center justify-end gap-[10px]">
            <Button variant="outline" onClick={() => setMode('idle')} disabled={applying}>Annuler</Button>
            <Button onClick={() => void apply()} disabled={applying}>
              {applying ? 'Application…' : `Appliquer (${draftTotal} pts)`}
            </Button>
          </div>
        </>
      ) : (
        <>
          <label className="flex flex-col gap-[6px] text-[11px] font-bold tracking-[0.06em] uppercase text-muted-foreground">
            Règles de correction globales <span className="normal-case tracking-normal font-medium text-muted-foreground opacity-80">facultatif</span>
            <Textarea
              className="p-[9px_11px] bg-[var(--naia-bg,transparent)]"
              value={rules}
              disabled={!editable}
              rows={2}
              placeholder="Ex. : pénaliser les fautes d'orthographe, exiger la méthode…"
              onChange={(e) => {
                setRules(e.target.value);
                setSavedRules(false);
              }}
              onBlur={() => void saveGlobalRules()}
            />
            {!savedRules && <span className="normal-case tracking-normal font-medium text-muted-foreground opacity-80">Modifié…</span>}
          </label>
          {editable && noted.length > 0 && (
            <div className="flex items-center justify-end gap-[10px]">
              <Button onClick={() => void suggest()} disabled={mode === 'loading'}>
                {mode === 'loading' ? 'Naïa réfléchit…' : '✨ Proposer barème & règles'}
              </Button>
            </div>
          )}
          {mode === 'loading' && slow && <SlowNotice text={CHAT_SLOW_NOTICE} />}
        </>
      )}
    </section>
  );
}
